import React from 'react';
import moment from 'moment';
import PropTypes from 'prop-types';
import { hasValue } from './maintenanceComplianceUtils';

const daysUntil = (value) => {
  if (!hasValue(value)) return null;
  const parsed = moment(value).startOf('day');
  if (!parsed.isValid()) return null;
  return parsed.diff(moment().startOf('day'), 'days');
};

export const ComplianceStatusBadge = ({ variant, label }) => {
  if (!label || label === '-') return null;
  return (
    <span className={`maintenance-compliance-badge maintenance-compliance-badge--${variant}`}>
      {variant === 'success' && (
        <svg className="maintenance-compliance-badge-icon" viewBox="0 0 16 16" fill="none" aria-hidden="true">
          <path d="M13.3 4.3a1 1 0 0 1 0 1.4l-5.5 5.5a1 1 0 0 1-1.4 0L2.7 7.5a1 1 0 1 1 1.4-1.4l2.6 2.6 4.8-4.8a1 1 0 0 1 1.4 0Z" fill="currentColor" />
        </svg>
      )}
      {(variant === 'warning' || variant === 'danger') && (
        <svg className="maintenance-compliance-badge-icon" viewBox="0 0 16 16" fill="none" aria-hidden="true">
          <path d="M8 1.5a6.5 6.5 0 1 0 0 13 6.5 6.5 0 0 0 0-13Zm.75 3.25a.75.75 0 0 0-1.5 0v3.5a.75.75 0 0 0 1.5 0v-3.5Zm-.75 6.25a.875.875 0 1 0 0-1.75.875.875 0 0 0 0 1.75Z" fill="currentColor" />
        </svg>
      )}
      {label}
    </span>
  );
};

ComplianceStatusBadge.propTypes = {
  variant: PropTypes.string.isRequired,
  label: PropTypes.string,
};

export const getExpiryBadge = (dateValue) => {
  const days = daysUntil(dateValue);
  if (days === null) return { variant: 'neutral', label: null };
  if (days < 0) return { variant: 'danger', label: 'Expired' };
  if (days <= 60) return { variant: 'warning', label: `${days} day${days === 1 ? '' : 's'}` };
  return { variant: 'success', label: 'Valid' };
};

export const getServiceOdometerBadge = (kmRemaining) => {
  if (!hasValue(kmRemaining)) return { variant: 'neutral', label: null };
  const km = Number(kmRemaining);
  if (!Number.isFinite(km)) return { variant: 'neutral', label: null };
  if (km < 0) return { variant: 'danger', label: 'Overdue' };
  if (km <= 1000) return { variant: 'warning', label: 'Due soon' };
  return { variant: 'success', label: 'On track' };
};

export const getServiceDateBadge = (dateValue) => {
  const days = daysUntil(dateValue);
  if (days === null) return { variant: 'neutral', label: null };
  if (days < 0) return { variant: 'danger', label: 'Overdue' };
  if (days <= 60) return { variant: 'warning', label: `${days} day${days === 1 ? '' : 's'}` };
  return { variant: 'success', label: 'On track' };
};

export const formatKm = (value) => {
  if (!hasValue(value)) return '-';
  const num = Number(value);
  if (!Number.isFinite(num)) return String(value);
  return `${num.toLocaleString()} km`;
};

export const formatRemainingMileageHint = (value) => {
  if (!hasValue(value)) return null;
  const km = Number(value);
  if (!Number.isFinite(km)) return null;
  const distance = Math.abs(km).toLocaleString();
  if (km < 0) return `Overdue by: ${distance} km`;
  return `Remaining mileage: ${distance} km`;
};

export const LOADING_LABEL = 'Loading…';

export const renderValueOrLoading = (loading, value, renderValue) => {
  if (loading && !hasValue(value)) return LOADING_LABEL;
  if (!hasValue(value)) return '-';
  return renderValue(value);
};
